import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Box, Button, CircularProgress, makeStyles, Typography } from '@material-ui/core'
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import { toFarsiDigits } from '../assets/utils'
import { getSiteSettings } from '../api/siteSettings'
import CardToCardDialog from '../components/billing/CardToCardDialog'

const CARD_NUMBER_DEFAULT = '6037997129445590'
const PHONE_1_DEFAULT = '09166551132'
const PHONE_2_DEFAULT = '09166015508'
const AMOUNT_DEFAULT = 37500000

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(3),
    direction: 'rtl',
  },
  card: {
    background: '#fff',
    borderRadius: 20,
    padding: theme.spacing(5, 4),
    textAlign: 'center',
    maxWidth: 420,
    width: '100%',
    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
  },
  successIcon: {
    fontSize: 80,
    color: '#2e7d32',
    marginBottom: theme.spacing(2),
  },
  failIcon: {
    fontSize: 80,
    color: '#c62828',
    marginBottom: theme.spacing(2),
  },
  successTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#1b5e20',
    marginBottom: theme.spacing(1),
  },
  failTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#b71c1c',
    marginBottom: theme.spacing(1),
  },
  infoRow: {
    background: '#f5f5f5',
    borderRadius: 10,
    padding: theme.spacing(1.5, 2),
    margin: theme.spacing(1, 0),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  infoLabel: {
    color: '#666',
    fontSize: 13,
  },
  infoValue: {
    fontWeight: 'bold',
    fontSize: 14,
    color: '#1a237e',
  },
  btn: {
    marginTop: theme.spacing(3),
    borderRadius: 10,
    padding: theme.spacing(1.2, 4),
    fontWeight: 'bold',
    fontSize: 15,
  },
  altBtn: {
    marginTop: theme.spacing(1),
    marginInline: theme.spacing(1),
    borderRadius: 10,
    padding: theme.spacing(1, 3),
    fontWeight: 'bold',
    fontSize: 14,
  },
  fallbackBox: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    borderRadius: 12,
    background: '#fff8e1',
    border: '1px solid #ffe082',
    textAlign: 'right',
  },
  fallbackTitle: {
    fontWeight: 'bold',
    color: '#8a6100',
    marginBottom: 6,
  },
  fallbackText: {
    color: '#5d4037',
    fontSize: 13,
    lineHeight: 2,
    marginBottom: 10,
  },
}))

const PaymentResult = () => {
  const classes = useStyles()
  const location = useLocation()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [siteCfg, setSiteCfg] = useState(null)
  const [cardOpen, setCardOpen] = useState(false)

  const params = new URLSearchParams(location.search)
  const statusParam = params.get('status')
  const ref = params.get('ref')
  const endDate = params.get('end_date')
  const years = Number(params.get('years')) === 2 ? 2 : 1

  useEffect(() => {
    getSiteSettings().then((d) => d && setSiteCfg(d))
  }, [])

  useEffect(() => {
    // اگر کاربر مستقیم به این صفحه آمد redirect کن
    if (!statusParam) {
      navigate('/')
      return
    }
    setLoading(false)

    // اگر پرداخت موفق بود، بعد از چند ثانیه به خانه برو
    if (statusParam === 'success') {
      const timer = setTimeout(() => {
        // reload کامل برای بروزرسانی اشتراک در state
        window.location.href = '/'
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [statusParam, navigate])

  if (loading) {
    return (
      <Box className={classes.root}>
        <CircularProgress />
      </Box>
    )
  }

  const isSuccess = statusParam === 'success'
  const isAlreadyDone = statusParam === 'already_done'
  const AMOUNT = Number(siteCfg?.payment?.subscription_fee) || AMOUNT_DEFAULT
  const CARD_NUMBER = siteCfg?.payment?.card_number || CARD_NUMBER_DEFAULT
  const PHONE_1 = siteCfg?.contact?.phone2 || PHONE_1_DEFAULT
  const PHONE_2 = siteCfg?.contact?.phone || PHONE_2_DEFAULT

  return (
    <Box className={classes.root}>
      <Box className={classes.card}>
        {isSuccess ? (
          <>
            <CheckCircleOutlineIcon className={classes.successIcon} />
            <Typography className={classes.successTitle}>پرداخت موفق!</Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              اشتراک شما با موفقیت فعال / تمدید شد
            </Typography>

            {ref && (
              <Box className={classes.infoRow}>
                <Typography className={classes.infoLabel}>کد پیگیری:</Typography>
                <Typography className={classes.infoValue} style={{ direction: 'ltr', fontSize: 12 }}>
                  {ref}
                </Typography>
              </Box>
            )}
            {endDate && (
              <Box className={classes.infoRow}>
                <Typography className={classes.infoLabel}>تاریخ پایان اشتراک:</Typography>
                <Typography className={classes.infoValue}>
                  {toFarsiDigits(endDate)}
                </Typography>
              </Box>
            )}

            <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 16 }}>
              در حال انتقال به صفحه اصلی...
            </Typography>
          </>
        ) : isAlreadyDone ? (
          <>
            <CheckCircleOutlineIcon className={classes.successIcon} style={{ color: '#1565c0' }} />
            <Typography className={classes.successTitle} style={{ color: '#1565c0' }}>
              پرداخت قبلاً ثبت شده
            </Typography>
            <Typography variant="body2" color="textSecondary">
              این تراکنش قبلاً در سیستم ثبت شده است. اشتراک شما فعال است.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              className={classes.btn}
              onClick={() => window.location.href = '/'}
            >
              بازگشت به خانه
            </Button>
          </>
        ) : (
          <>
            <ErrorOutlineIcon className={classes.failIcon} />
            <Typography className={classes.failTitle}>پرداخت ناموفق</Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
              {statusParam === 'failed' && 'پرداخت توسط کاربر لغو شد یا با خطا مواجه شد.'}
              {statusParam === 'verify_failed' && 'تأیید تراکنش از بانک با مشکل مواجه شد. مبلغی کسر نشده است.'}
              {statusParam === 'amount_mismatch' && 'مبلغ تراکنش با مبلغ اشتراک مطابقت ندارد. با پشتیبانی تماس بگیرید.'}
              {!['failed', 'verify_failed', 'amount_mismatch'].includes(statusParam) &&
                'خطایی رخ داد. لطفاً مجدداً تلاش کنید یا با پشتیبانی تماس بگیرید.'
              }
            </Typography>
            <Button
              variant="contained"
              color="primary"
              className={classes.btn}
              onClick={() => window.location.href = '/'}
            >
              بازگشت و تلاش مجدد
            </Button>

            {/* راهِ جایگزین — فقط وقتی پرداخت از درگاه شکست خورده */}
            <Box className={classes.fallbackBox}>
              <Typography className={classes.fallbackTitle}>
                پرداخت از درگاه انجام نشد — راهِ جایگزین: کارت به کارت
              </Typography>
              <Typography className={classes.fallbackText}>
                اگر اتصال به درگاه بانکی برای شما ممکن نیست، می‌توانید مبلغِ اشتراک را
                {' '}{years === 2 ? 'دو‌ساله' : 'یک‌ساله'} ({toFarsiDigits(AMOUNT * years)} ریال)
                {' '}به شمارهٔ کارت سامانه واریز کنید. پس از واریز، شناسهٔ ملی و نام شرکت خود را
                به یکی از شماره‌های پشتیبانی پیامک کنید تا اشتراک شما فعال شود.
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                className={classes.altBtn}
                onClick={() => setCardOpen(true)}
              >
                مشاهدهٔ شماره کارت و مراحل پرداخت
              </Button>
            </Box>

            <CardToCardDialog
              open={cardOpen}
              onClose={() => setCardOpen(false)}
              cardNumber={CARD_NUMBER}
              phones={[PHONE_1, PHONE_2]}
              amount={AMOUNT * years}
              years={years}
            />
          </>
        )}
      </Box>
    </Box>
  )
}

export default PaymentResult